import { DocumentData, DocumentSnapshot, Timestamp } from "firebase/firestore";
import { Option, TemperatureThresholds, DEFAULT_TEMPERATURE_THRESHOLDS } from './interfaces';
import { EventLogActionType, EventLogSource, JobFrequency, JobStatus, VisitType, WorkListType, 
  ContractLineStatus, OutletType, SampleType, IssueReason, AccessTime, TemperatureTakenFrom, TankShape, TankStatus, TankMaterial, TankLocationType, PipeMaterial, TankSwitchType, TankConditionLevel,
  DisinfectionMethod, DisinfectantType, NeutralisingAgent } from "./enums";

export class Utils {
  // to show images in devMode
  static WEB_APP_URL_FOR_IMAGES = "https://proeconomy-tetras.web.app/";

  static UNDEFINED_SORT_NUMBER = 99999;

  // Color constants for result indicators
  static readonly RED_COLOR = '#dc3545';
  static readonly GREEN_COLOR = '#198754';
  static readonly PURPLE_COLOR = '#6f42c1';
  static readonly AMBER_COLOR = '#fd7e14';
  static readonly WHITE_COLOR = 'white';
  static readonly LIGHT_BLUE_COLOR = '#add8e6';
  static readonly LIGHT_GREY_COLOR = '#d3d3d3';

  // Chlorine Dioxide band: Red (=0 or >=redMin), Amber (0-greenMin or greenMax-redMin), Green (greenMin-greenMax)
  static getChlorineDioxideBand(value: number, thresholds: TemperatureThresholds): 'red' | 'amber' | 'green' {
    const numValue = Number(value); // values from Firestore can come through as strings
    const greenMin = thresholds.chlorineDioxideGreenMin ?? DEFAULT_TEMPERATURE_THRESHOLDS.chlorineDioxideGreenMin!;
    const greenMax = thresholds.chlorineDioxideGreenMax ?? DEFAULT_TEMPERATURE_THRESHOLDS.chlorineDioxideGreenMax!;
    const redMin = thresholds.chlorineDioxideRedMin ?? DEFAULT_TEMPERATURE_THRESHOLDS.chlorineDioxideRedMin!;
    if (numValue === 0 || numValue >= redMin) {
      return 'red';
    }
    if (numValue >= greenMin && numValue <= greenMax) {
      return 'green';
    }
    return 'amber';
  }

  static getChlorineDioxideColor(value: number, thresholds: TemperatureThresholds): string {
    switch (this.getChlorineDioxideBand(value, thresholds)) {
      case 'red': return this.RED_COLOR;
      case 'green': return this.GREEN_COLOR;
      default: return this.AMBER_COLOR;
    }
  }

  static isChlorineDioxideOutOfRange(value: number, thresholds: TemperatureThresholds): boolean {
    return this.getChlorineDioxideBand(value, thresholds) !== 'green';
  }

  static stringFromArray(arr: string[], separator: string) {
    let str = '';
    for (let i = 0; i < arr.length; i++) {
      if (i > 0) {
        str += separator;
      }
      str += arr[i];
    }
    return str;
  }

  static escapeCsvField(value: string): string {
    if (value.includes(',') || value.includes('"') || value.includes('\n')) {
      return `"${value.replace(/"/g, '""')}"`;
    }
    return value;
  }

  // Date and Time functions
  // Use ISO format: yyyy-mm-ddThh:mi:ss.zzzZ. Locale format can vary. Date returned from the Timestamp is UDT!

  // Convert given timestamp to Date
  static timestampToDateWithoutLocalOffset(timestamp: Timestamp): Date {
    return new Date(timestamp.seconds * 1000);
  }

  // Convert given timestamp to Date in local time
  static timestampToDate(timestamp: Timestamp): Date {
    return this.timestampSecondsToDate(timestamp.seconds);
  }

  // Convert given number of seconds (from a timestamp) to Date in local time
  static timestampSecondsToDate(seconds: number): Date {
    const udtDate: Date = new Date(seconds * 1000);
    return this.udtDateToLocalDate(udtDate);
  }

  // Get current Date in local time
  static currentDate(): Date {
    return this.udtDateToLocalDate(new Date());
  }

  // Get current Date, adjusted to local time
  static udtDateToLocalDate(udtDate: Date): Date {
    const localToUdtOffsetInMinutes = udtDate.getTimezoneOffset();
    return new Date(udtDate.getTime() - localToUdtOffsetInMinutes * 60000);  // Uses minus as we are converting from UDT to Local
  }

  // Output format : dd-mm-yyyy
  static timestampToDateString(timestamp: Timestamp, delimiter: string): string {
    const dateStr: string = this.timestampToDate(timestamp).toISOString();
    const dd: string = dateStr.substring(8,10);
    const mm: string = dateStr.substring(5,7); 
    const yyyy: string = dateStr.substring(0,4);
    return `${dd}${delimiter}${mm}${delimiter}${yyyy}`;
  }

  // Output format : hh:mi:ss
  static timestampToTimeString(timestamp: Timestamp): string {
    return this.timestampToDate(timestamp).toISOString().substring(11,19);
  }

  // Output format : hh:mi
  static timestampToShortTimeString(timestamp: Timestamp): string {
    return this.timestampToDate(timestamp).toISOString().substring(11,16);
  }

  // Output format : dd*mm*yyyy    (where * represents a delimiter)
  static currentDateString(delimiter: string | null, yearFirst?: boolean): string {
    const dateStr: string = this.currentDate().toISOString();
    const dd: string = dateStr.substring(8,10);
    const mm: string = dateStr.substring(5,7); 
    const yyyy: string = dateStr.substring(0,4);
    if (!delimiter || delimiter.length != 1) {
      delimiter = '';       
    }
    if (yearFirst) {
      return `${yyyy}${delimiter}${mm}${delimiter}${dd}`;
    } else {
      return `${dd}${delimiter}${mm}${delimiter}${yyyy}`;
    }
  }

  // Output format : hh*mi*ss    (where * represents a delimiter)
  static currentTimeString(delimiter: string | null): string {
    const dateStr: string = this.currentDate().toISOString();
    const hh: string = dateStr.substring(11,13);
    const mi: string = dateStr.substring(14,16); 
    const ss: string = dateStr.substring(17,19);
    if (!delimiter || delimiter.length != 1) {
      delimiter = '';       
    }
    return `${hh}${delimiter}${mi}${delimiter}${ss}`;
  }

  // Output format : ddmmyyyyhhmiss
  static currentDateTimeString(): string {
    return `${this.currentDateString(null)}${this.currentTimeString(null)}`;
  }

  // ----

  static cleanString(str: string | undefined): string {
    return str ? str : '';
  }

  static xmlString(str: string | undefined): string {
    return Utils.cleanString(str).replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  static escapeHtml(str: string | undefined): string {
    return Utils.cleanString(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  // Empty barcode values are set to '-' ('' is equivalent to undefined). Remove the '-' when retrieving the value.
  static cleanBarcode(str: string | undefined): string | undefined {
    if (!!str && str === '-') {
      return '';
    } else {
      return str;
    }
  }
  
  static isUpperCase(str: string): boolean  {
    return str == str.toUpperCase() && str != str.toLowerCase();
  }

  static camelToLabel(str: string): string {
    let outStr = '';
    for (const c of str) {
      if (outStr.length == 0) {
        outStr = c.toUpperCase();
      } else {
        if (this.isUpperCase(c)) {
          outStr += ' ';
        }
        outStr += c;
      }
    }
    return outStr;
  }

  static isEven(n: number) {
    return n % 2 == 0;
  }
  
  // Return just the key-value pairs from a DocumentData record returned from DocumentSnapshot<DocumentData>.data().
  static cleanDocData(doc: DocumentSnapshot<DocumentData>): Record<string, any> {
    const record: Record<string, any> = {};
    const data:  DocumentData | undefined = doc.data();
    if (data) {
      const keys = Object.keys(data);
      for (const key of keys) { 
        record[key] = data[key];
      }
    }
    return record;
  }

  static roundNumber(number: number) {
    return Math.round((number + Number.EPSILON) * 100) / 100;
  }

  static movePrimaryTestsToMap(outletRecord: Record<string, any>): Record<string, any> {
    const primaryTests: Record<string, any> = {};
    primaryTests['copper'] = outletRecord['copper'];
    primaryTests['silver'] = outletRecord['silver'];
    primaryTests['legionella'] = outletRecord['legionella'];
    primaryTests['pseudomonas'] = outletRecord['pseudomonas'];
    primaryTests['tvc22'] = outletRecord['tvc22'];
    primaryTests['tvc37'] = outletRecord['tvc37'];
    delete outletRecord['copper'];
    delete outletRecord['silver'];
    delete outletRecord['legionella'];
    delete outletRecord['pseudomonas'];
    delete outletRecord['tvc22'];
    delete outletRecord['tvc37'];
    outletRecord['primaryTests']  = primaryTests;
    return outletRecord;
  }

  static compare(a: number | string | boolean | Timestamp | Date, b: number | string | boolean | Timestamp | Date, isAsc: boolean) {
    return (a < b ? -1 : a > b ? 1 : 0) * (isAsc ? 1 : -1);
  }

  static getOutletOutputTemperatureRange(outletType: OutletType): { minTemp: number; maxTemp: number; rangeName: string } | null {
    switch(outletType) {
      case OutletType.MT:
        return { minTemp: 39, maxTemp: 43, rangeName: 'MT' };
      case OutletType.CT:
        return { minTemp: 39, maxTemp: 43, rangeName: 'CT' };
      case OutletType.HT:
        return { minTemp: 39, maxTemp: 43, rangeName: 'HT' };
      case OutletType.Shower:
        return { minTemp: 39, maxTemp: 43, rangeName: 'Shower' };
      case OutletType.Bath:
        return { minTemp: 42, maxTemp: 46, rangeName: 'Bath' };
      case OutletType.Bidet:
        return { minTemp: 36, maxTemp: 40, rangeName: 'Bidet' };
      default:
        return null;
    }
  }

  static getIssueReasonsDisplay(issueReasons: string[] | undefined): string {
    if (!issueReasons || issueReasons.length === 0) return '';
    
    const displayValues = issueReasons.map(reason => {
      // If reason is "Other", just display "Other"
      if (reason == IssueReason.Other) {
        return 'Other';
      }
      // Try to find in regular issue reasons first
      let option = this.tmvIssueReasons.find(opt => opt.value === reason);
      // If not found, try temperature issue reasons
      if (!option) {
        option = this.temperatureIssueReasons.find(opt => opt.value === reason);
      }
      // If not found, try sampling issue reasons
      if (!option) {
        option = this.samplingIssueReasons.find(opt => opt.value === reason);
      }
      // If not found, try flushing issue reasons
      if (!option) {
        option = this.flushingIssueReasons.find(opt => opt.value === reason);
      }
      // If not found, try shower clean issue reasons
      if (!option) {
        option = this.showerCleanIssueReasons.find(opt => opt.value === reason);
      }
      // If not found, try tank inspection issue reasons
      if (!option) {
        option = this.tankInspectionIssueReasons.find(opt => opt.value === reason);
      }
      if (!option) {
        option = this.tankCleanIssueReasons.find(opt => opt.value === reason);
      }
      return option ? option.viewValue : reason;
    });
    
    return displayValues.join(', ');
  }

  static chunkArray<T>(arr: T[], size: number): T[][] {
    const chunks: T[][] = [];
    for (let i = 0; i < arr.length; i += size) {
      chunks.push(arr.slice(i, i + size));
    }
    return chunks;
  }

  static getColorByJobStatus(status: JobStatus): string {
    switch(status) {
      case JobStatus.ProvisionallyScheduled: {
        return "#fffeb9";
      }
      case JobStatus.Scheduled: {
        return "#f7bc3d";
      }
      case JobStatus.Notified: {
        return "#8dd8fa";
      }
      case JobStatus.InProgress: {
        return "#e79aff";
      }
      case JobStatus.SheetsCreated: {
        return "#ffb5c0";
      }
      case JobStatus.Completed: {
        return "#45c983";
      }
      case JobStatus.ReportSent: {
        return "#696bffff";
      }
      case JobStatus.Invoiced: {
        return "#2b8e8bff";
      }
      case JobStatus.Off:
      case JobStatus.OffNotPayable: {
        return "#B8B8B8";
      }
      case JobStatus.Cancelled: {
        return "#fe5757";
      }
      default:
        return "";
    }
  }

  static withinTheSameDay(dateOne: Date | null, dateTwo: Date | null) {
    if (!dateOne || !dateTwo) return false;
    return dateOne.getFullYear() === dateTwo.getFullYear() && dateOne.getMonth() === dateTwo.getMonth() && dateOne.getDate() === dateTwo.getDate();
  }

  // Normalizes a date to the last millisecond of that day so date range filters/loops include the whole end day
  // (datepickers reset the time to midnight when a new date is selected, which would otherwise exclude that day).
  // Accepts null so callers can pass a nullable endDate directly (only invoked once it has been confirmed non-null).
  static endOfDay(date: Date | null): Date {
    const result = new Date(date!);
    result.setHours(23, 59, 59, 999);
    return result;
  }

  // Convert an Excel date value to a JavaScript Date. Excel stores dates as the
  // number of days since 1899-12-30 in local time. After converting the serial
  // number, adjust by the local timezone offset so the resulting timestamp
  // represents midnight of the local day in UTC.
  static parseExcelDate(value: any): Date | null {
    if (!value) {
      return null;
    }
    if (value instanceof Date) {
      return value;
    }
    if (typeof value === 'number' || (typeof value === 'string' && /^\d+(\.\d+)?$/.test(value))) {
      const excelEpoch = new Date(Date.UTC(1899, 11, 30));
      const serial = typeof value === 'number' ? value : parseFloat(value);
      const date = new Date(excelEpoch.getTime() + serial * 86400000);
      return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
    } else if (typeof value === 'string') {
      // UK format dd/mm/yyyy or dd-mm-yyyy, which Date.parse would otherwise misread as US mm/dd/yyyy (or reject).
      const ukMatch = value.trim().match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
      if (ukMatch) {
        const day = parseInt(ukMatch[1], 10);
        const month = parseInt(ukMatch[2], 10);
        const year = parseInt(ukMatch[3], 10);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
          return new Date(year, month - 1, day);
        }
        return null;
      }
      const parsed = Date.parse(value);
      return isNaN(parsed) ? null : new Date(parsed);
    }
    return null;
  }

  // Number formatting utility
  static restrictDecimals(value: any): any {
    if (value === undefined || value === null) {
      return null;
    }
    
    if (typeof value === 'number') {
      return Math.round(value * 100) / 100;
    }
    
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        return Math.round(parsed * 100) / 100;
      }
    }
    
    return value;
  }

  // Number formatting utility - restricts to 4 decimal places
  static restrictDecimalsTo4(value: any): any {
    if (value === undefined || value === null) {
      return null;
    }
    
    if (typeof value === 'number') {
      return Math.round(value * 10000) / 10000;
    }
    
    if (typeof value === 'string') {
      const parsed = parseFloat(value);
      if (!isNaN(parsed)) {
        return Math.round(parsed * 10000) / 10000;
      }
    }
    
    return value;
  }

  static outletStatus: Option[] = [
    {value: 'OK', viewValue: 'OK'},
    {value: 'Covid', viewValue: 'No Access - Isolation'},
    {value: 'Room in Use', viewValue: 'No Access - Room in Use'},
    {value: 'Not Working', viewValue: 'Not Working'},
    {value: 'Removed', viewValue: 'Removed'},
    {value: 'Other', viewValue: 'Unavailable - Other Reason'}
  ];

  static sampleStatus: Option[] = [
    {value: 'ToDo', viewValue: 'To Do'},
    {value: 'Done', viewValue: 'Done'},
    {value: 'Not Taken', viewValue: 'Not Taken'}
  ];

  static flushStatus: Option[] = [
    {value: 'Pre', viewValue: 'Pre-Flush'},
    {value: 'Post', viewValue: 'Post-Flush'},
    {value: 'Both', viewValue: 'Both'}
  ];

  static sampleType: Option[] = [
    {value: 'Primary', viewValue: 'Primary'},
    {value: 'Secondary', viewValue: 'Secondary'},
    {value: 'Water Quality', viewValue: 'Water Quality'}
  ];

  static outletTypes: Option[] = [
    {value: OutletType.CT, viewValue: 'CT'},
    {value: OutletType.HT, viewValue: 'HT'},
    {value: OutletType.MT, viewValue: 'MT'},
    {value: OutletType.Bath, viewValue: 'Bath'},
    {value: OutletType.Bidet, viewValue: 'Bidet'},
    {value: OutletType.Shower, viewValue: 'Shower'},
    {value: OutletType.Tank, viewValue: 'Tank'},
    {value: OutletType.HeatingSystem, viewValue: 'Heating System'},
    {value: OutletType.Softener, viewValue: 'Softener'},
    {value: OutletType.ExpansionVessel, viewValue: 'Expansion Vessel'},
    {value: OutletType.ClosedCircuitSystem, viewValue: 'Closed Circuit System'}
  ];

  static sampleTypes: Option[] = [
    {value: SampleType.Cold, viewValue: 'COLD'},
    {value: SampleType.Hot, viewValue: 'HOT'},
    {value: SampleType.Blended, viewValue: 'BLENDED'}
  ];

  static accessTimes: Option[] = [
    {value: AccessTime.One, viewValue: '1 (0-5 mins)'},
    {value: AccessTime.Two, viewValue: '2 (5-10 mins)'},
    {value: AccessTime.Three, viewValue: '3 (10+ mins)'}
  ];

  static tankShapes: Option[] = [
    {value: TankShape.Rectangular, viewValue: 'Rectangular'},
    {value: TankShape.Cube, viewValue: 'Cube'},
    {value: TankShape.VerticalCylinder, viewValue: 'Vertical Cylinder'},
    {value: TankShape.HorizontalCylinder, viewValue: 'Horizontal Cylinder'}
  ];

  static tankStatuses: Option[] = [
    {value: TankStatus.Online, viewValue: 'Online'},
    {value: TankStatus.Offline, viewValue: 'Offline'}
  ];

  static tankMaterials: Option[] = [
    {value: TankMaterial.BlackPlastic, viewValue: 'Black Plastic'},
    {value: TankMaterial.GRP, viewValue: 'Glass Reinforced Plastic (GRP)'},
    {value: TankMaterial.MildSteel, viewValue: 'Mild Steel'},
    {value: TankMaterial.OtherUnsuitableMaterial, viewValue: 'Other - Unsuitable Material'},
    {value: TankMaterial.Other, viewValue: 'Other'}
  ];

  static tankLocationTypes: Option[] = [
    {value: TankLocationType.Indoor, viewValue: 'Indoor'},
    {value: TankLocationType.Outdoor, viewValue: 'Outdoor'}
  ];

  // Pipe material, reused across Inlet/Outlet/Warning Pipe/Overflow (distinct from tank body TankMaterial)
  static pipeMaterials: Option[] = [
    {value: PipeMaterial.Copper, viewValue: 'Copper'},
    {value: PipeMaterial.Steel, viewValue: 'Steel'},
    {value: PipeMaterial.Plastic, viewValue: 'Plastic'},
    {value: PipeMaterial.Lead, viewValue: 'Lead'},
    {value: PipeMaterial.Other, viewValue: 'Other'}
  ];

  static pipeSizeUnits: Option[] = [
    {value: 'inch', viewValue: 'inch'},
    {value: 'mm', viewValue: 'mm'},
    {value: 'cm', viewValue: 'cm'},
    {value: 'm', viewValue: 'm'}
  ];

  static tankSwitchTypes: Option[] = [
    {value: TankSwitchType.None, viewValue: 'None'},
    {value: TankSwitchType.Ballvalve, viewValue: 'Ball Valve'},
    {value: TankSwitchType.Keraflo, viewValue: 'Keraflo'},
    {value: TankSwitchType.SolenoidValve, viewValue: 'Solenoid Valve'},
    {value: TankSwitchType.Level, viewValue: 'Level'},
    {value: TankSwitchType.Other, viewValue: 'Other'}
  ];

  static disinfectionMethods: Option[] = [
    {value: DisinfectionMethod.HardSurfaceSpray, viewValue: 'Hard Surface Spray'},
    {value: DisinfectionMethod.FillAndSoak, viewValue: 'Fill And Soak'}
  ];

  static disinfectantTypes: Option[] = [
    {value: DisinfectantType.SodiumHypochlorite, viewValue: 'Sodium Hypochlorite'},
    {value: DisinfectantType.ChlorineDioxide, viewValue: 'Chlorine Dioxide'},
    {value: DisinfectantType.SilverStabilisedHydrogenPeroxide, viewValue: 'Silver Stabilised Hydrogen Peroxide'},
    {value: DisinfectantType.Other, viewValue: 'Other'}
  ];

  static neutralisingAgents: Option[] = [
    {value: NeutralisingAgent.None, viewValue: 'None'},
    {value: NeutralisingAgent.SodiumThiosulphate, viewValue: 'Sodium Thiosulphate'},
    {value: NeutralisingAgent.Other, viewValue: 'Other'}
  ];

  // Internal condition level, reused across Scale/Sediment/Corrosion
  static tankConditionLevels: Option[] = [
    {value: TankConditionLevel.None, viewValue: 'None'},
    {value: TankConditionLevel.Light, viewValue: 'Light'},
    {value: TankConditionLevel.Moderate, viewValue: 'Moderate'},
    {value: TankConditionLevel.Heavy, viewValue: 'Heavy'}
  ];

  // Cuboid (Rectangular/Cube) capacity in litres from mm dimensions
  static calculateCuboidCapacityLitres(lengthMm: any, widthMm: any, heightMm: any): number | null {
    const l = Number(lengthMm);
    const w = Number(widthMm);
    const h = Number(heightMm);
    if (!lengthMm || !widthMm || !heightMm || isNaN(l) || isNaN(w) || isNaN(h) || l <= 0 || w <= 0 || h <= 0) {
      return null;
    }
    return Math.round((l * w * h) / 10000) / 100;
  }

  // Cylinder (Vertical/Horizontal) capacity in litres from mm diameter and length/height
  static calculateCylinderCapacityLitres(diameterMm: any, lengthMm: any): number | null {
    const d = Number(diameterMm);
    const len = Number(lengthMm);
    if (!diameterMm || !lengthMm || isNaN(d) || isNaN(len) || d <= 0 || len <= 0) {
      return null;
    }
    return Math.round((Math.PI * d * d * len) / 40000) / 100;
  }

  static tmvIssueReasons: Option[] = [
    {value: IssueReason.NoAccessRoomInUse, viewValue: 'No Access - Room In Use'},
    {value: IssueReason.NoAccessToIsolationTmv, viewValue: 'No Access To Isolation/TMV'},
    {value: IssueReason.LocalIsolationValveFailureHot, viewValue: 'Local Isolation Valve Failure/Not Present (HOT)'},
    {value: IssueReason.LocalIsolationValveFailureCold, viewValue: 'Local Isolation Valve Failure/Not Present (COLD)'},
    {value: IssueReason.LocalIsolationValveFailureReturn, viewValue: 'Local Isolation Valve Failure/Not Present (RETURN)'},
    {value: IssueReason.UnableToAdjustTempHotInletDepressed, viewValue: 'Unable To Adjust Temperature (Hot Temp Inlet Depressed)'},
    {value: IssueReason.UnableToAdjustTempCartridgeFailure, viewValue: 'Unable To Adjust Temperature (Thermostatic Cartridge Failure)'},
    {value: IssueReason.DepressedReturnTemperature, viewValue: 'Depressed Return Temperature'},
    {value: IssueReason.FailsafeTestFailure, viewValue: 'Failsafe Test Failure'},
    {value: IssueReason.UnableToRemoveTmvTmt, viewValue: 'Unable To Remove TMV/TMT'},
    {value: IssueReason.OutletHasBeenRemoved, viewValue: 'Outlet Has Been Removed'},
    {value: IssueReason.Other, viewValue: 'Other (Please Complete Non-Compliance/Service Notes)'}
  ];

  static temperatureIssueReasons: Option[] = [
    {value: IssueReason.NoAccessRoomInUse, viewValue: 'No Access - Room In Use'},
    {value: IssueReason.ElevatedColdTemperature, viewValue: 'Elevated Cold Temperature'},
    {value: IssueReason.NoAccessToPipework, viewValue: 'No Access To Pipework'},
    {value: IssueReason.ElevatedHotTemperature, viewValue: 'Elevated Hot Temperature'},
    {value: IssueReason.DepressedHotTemperature, viewValue: 'Depressed Hot Temperature'},
    {value: IssueReason.ElevatedMixedTemperature, viewValue: 'Elevated Mixed Temperature'},
    {value: IssueReason.DepressedMixedTemperature, viewValue: 'Depressed Mixed Temperature'},
    {value: IssueReason.ExceedsChlorineDioxideUpperLimit, viewValue: 'Exceeds Chlorine Dioxide Upper Limit'},
    {value: IssueReason.BelowChlorineDioxideLowerLimit, viewValue: 'Below Chlorine Dioxide Lower Limit'},
    {value: IssueReason.Other, viewValue: 'Other (Please Complete Non-Compliance/Service Notes)'}
  ];

  static samplingIssueReasons: Option[] = [
    {value: IssueReason.SamplingCopper, viewValue: 'Copper'},
    {value: IssueReason.SamplingSilver, viewValue: 'Silver'},
    {value: IssueReason.SamplingTvc22, viewValue: 'TVC 22'},
    {value: IssueReason.SamplingTvc37, viewValue: 'TVC 37'},
    {value: IssueReason.SamplingLegionella, viewValue: 'Legionella'},
    {value: IssueReason.SamplingPseudomonas, viewValue: 'Pseudomonas'},
    {value: IssueReason.SamplingEcoli, viewValue: 'E.coli'},
    {value: IssueReason.SamplingTotalColiforms, viewValue: 'Total Coliforms'}
  ];

  static flushingIssueReasons: Option[] = [
    {value: IssueReason.NoAccessRoomInUse, viewValue: 'No Access - Room In Use'},
    {value: IssueReason.OutletIsolatedNotWorking, viewValue: 'Outlet Isolated/Not Working'},
    {value: IssueReason.NoAccessDeniedAccess, viewValue: 'No Access - Denied Access'},
    {value: IssueReason.OutletBlocked, viewValue: 'Outlet Blocked'},
    {value: IssueReason.OutletRemoved, viewValue: 'Outlet Removed'},
    {value: IssueReason.Other, viewValue: 'Other (Please Complete Non-Compliance/Service Notes)'}
  ];

  static showerCleanIssueReasons: Option[] = [
    {value: IssueReason.NoAccessRoomInUse, viewValue: 'No Access - Room In Use'},
    {value: IssueReason.OutletIsolatedNotWorking, viewValue: 'Outlet Isolated/Not Working'},
    {value: IssueReason.NoAccessDeniedAccess, viewValue: 'No Access - Denied Access'},
    {value: IssueReason.OutletBlocked, viewValue: 'Outlet Blocked'},
    {value: IssueReason.OutletRemoved, viewValue: 'Outlet Removed'},
    {value: IssueReason.ShowerHeavyScaleDepositsOnHeadNozzles, viewValue: 'Heavy Scale Deposits On Shower Head/Nozzles'},
    {value: IssueReason.ShowerDamagedOrSplitHose, viewValue: 'Damaged Or Split Shower Hose'},
    {value: IssueReason.ShowerCrackedOrDamagedHead, viewValue: 'Cracked Or Damaged Shower Head'},
    {value: IssueReason.ShowerPoorOrUnevenSprayPattern, viewValue: 'Poor Or Uneven Spray Pattern'},
    {value: IssueReason.ShowerHoseExcessivelyLong, viewValue: 'Shower Hose Excessively Long'},
    {value: IssueReason.ShowerHoseArrangementTrapsWater, viewValue: 'Hose Arrangement Allows Water To Remain Trapped After Use'},
    {value: IssueReason.ShowerHeadHoseNotSuitableForApplication, viewValue: 'Shower Head/Hose Not Suitable For Application'},
    {value: IssueReason.Other, viewValue: 'Other (Please Complete Non-Compliance/Service Notes)'}
  ];

  static tankInspectionIssueReasons: Option[] = [
    {value: IssueReason.NoAccessRoomInUse, viewValue: 'No Access - Room In Use'},
    {value: IssueReason.TankOfflineNotDrainedDown, viewValue: 'Tank Offline But Not Drained Down'},
    {value: IssueReason.TankUnsuitableMaterial, viewValue: 'Tank Constructed From An Unsuitable Material'},
    {value: IssueReason.TankInsufficientAccessForCleaning, viewValue: 'Insufficient Access For Cleaning'},
    {value: IssueReason.TankLidNotFitted, viewValue: 'Tank Lid Not Fitted'},
    {value: IssueReason.TankNoAirVentFitted, viewValue: 'No Air Vent Fitted'},
    {value: IssueReason.TankAirVentNotScreened, viewValue: 'Air Vent Is Not Fitted With Suitable Corrosion-Resistant Mesh'},
    {value: IssueReason.TankInsufficientInletMaterial, viewValue: 'Insufficient Inlet Material'},
    {value: IssueReason.TankInsufficientInletOutletCrossFlow, viewValue: 'Insufficient Inlet / Outlet Cross Flow'},
    {value: IssueReason.TankNoInletIsolationValveFitted, viewValue: 'No Inlet Isolation Valve Fitted'},
    {value: IssueReason.TankInletIsolationValveNotOperational, viewValue: 'Inlet Isolation Valve Not Operational'},
    {value: IssueReason.TankInsufficientInletInsulation, viewValue: 'Insufficient Pipework Insulation On Inlet'},
    {value: IssueReason.TankInsufficientOutletMaterial, viewValue: 'Insufficient Outlet Material'},
    {value: IssueReason.TankNoOutletIsolationValveFitted, viewValue: 'No Outlet Isolation Valve Fitted'},
    {value: IssueReason.TankOutletIsolationValveNotOperational, viewValue: 'Outlet Isolation Valve Not Operational'},
    {value: IssueReason.TankInsufficientOutletInsulation, viewValue: 'Insufficient Pipework Insulation On Outlet'},
    {value: IssueReason.TankNoWarningPipeFitted, viewValue: 'No Warning Pipe Fitted'},
    {value: IssueReason.TankInsufficientWarningPipeMaterial, viewValue: 'Insufficient Warning Pipe Material'},
    {value: IssueReason.TankWarningPipeInterconnection, viewValue: 'Warning Pipe Interconnection'},
    {value: IssueReason.TankWarningPipeNotScreened, viewValue: 'Warning Pipe Is Not Fitted With Suitable Corrosion-Resistant Mesh'},
    {value: IssueReason.TankInsufficientOverflowMaterial, viewValue: 'Insufficient Overflow Pipe Material'},
    {value: IssueReason.TankOverflowInterconnection, viewValue: 'Overflow Pipe Interconnection'},
    {value: IssueReason.TankOverflowNotScreened, viewValue: 'Overflow Pipe Is Not Fitted With Suitable Corrosion-Resistant Mesh'},
    {value: IssueReason.TankOpenVentIncorrectlyConfigured, viewValue: 'Open Vent From Hot Water Generator Incorrectly Configured'},
    {value: IssueReason.TankDeadEndNoted, viewValue: 'Dead End Noted On The Tank'},
    {value: IssueReason.TankSuspectedCrossContamination, viewValue: 'Suspected Cross-Contamination Noted On The Tank'},
    {value: IssueReason.TankHeavyScaleObserved, viewValue: 'Heavy Scale Observed'},
    {value: IssueReason.TankHeavySedimentObserved, viewValue: 'Heavy Sediment Observed'},
    {value: IssueReason.TankHeavyCorrosionObserved, viewValue: 'Heavy Corrosion Observed'},
    {value: IssueReason.TankStagnantRedundantOrOversized, viewValue: 'Tank Stagnant, Redundant Or Oversized'},
    {value: IssueReason.TankHollowLidSupportsFitted, viewValue: 'Hollow Lid Supports Fitted To Tank'},
    {value: IssueReason.TankRelineInPoorCondition, viewValue: 'Tank Reline In Poor Condition'},
    {value: IssueReason.Other, viewValue: 'Other (Please Complete Non-Compliance/Service Notes)'}
  ];

  static tankCleanIssueReasons: Option[] = [
    {value: IssueReason.NoAccessRoomInUse, viewValue: 'No Access - Room In Use'},
    {value: IssueReason.TankNoInletIsolationValveFitted, viewValue: 'No Inlet Isolation Valve Fitted'},
    {value: IssueReason.TankInletIsolationValveNotOperational, viewValue: 'Inlet Isolation Valve Not Operational'},
    {value: IssueReason.TankNoOutletIsolationValveFitted, viewValue: 'No Outlet Isolation Valve Fitted'},
    {value: IssueReason.TankOutletIsolationValveNotOperational, viewValue: 'Outlet Isolation Valve Not Operational'},
    {value: IssueReason.TankSwitchValveNotOperational, viewValue: 'Inlet Switch Valve Not Operating Correctly'},
    {value: IssueReason.TankAirVentNotScreened, viewValue: 'Air Vent Is Not Fitted With Suitable Corrosion-Resistant Mesh'},
    {value: IssueReason.Other, viewValue: 'Other (Please Complete Non-Compliance/Service Notes)'}
  ];

  static temperatureTakenFromOptions: Option[] = [
    {value: TemperatureTakenFrom.DirectWater, viewValue: 'Direct Water'},
    {value: TemperatureTakenFrom.BrassFitting, viewValue: 'Brass Fitting'},
    {value: TemperatureTakenFrom.CopperPipe, viewValue: 'Copper Pipe'}
  ];

  static controlPanelTypeOptions: Option[] = [ {value: 'kb1', viewValue: 'KB1'}, {value: 'kb2', viewValue: 'KB2'}, {value: 'kb3', viewValue: 'KB3'},
    {value: 'ray1', viewValue: 'Ray 1'}, {value: 'ray2', viewValue: 'Ray 2'}, {value: 'ray3', viewValue: 'Ray 3'} ];
  static manifoldTypeOptions: Option[] = [ {value: '2', viewValue: '2 Pod'}, {value: '6', viewValue: '6 Pod'}, {value: '12', viewValue: '12 Pod'}, {value: '18', viewValue: '18 Pod'}, {value: '24', viewValue: '24 Pod'},
    {value: 'ray1Body', viewValue: 'Ray 1 Body'}, {value: 'ray2Body', viewValue: 'Ray 2 Body'}, {value: 'ray3Body', viewValue: 'Ray 3 Body'} ];
  static flowMeterTypeOptions: Option[] = [ {value: 'mechanical', viewValue: 'Mechanical'}, {value: 'ultrasonic', viewValue: 'Ultrasonic'}, {value: 'flowSwitch', viewValue: 'Flow Switch'} ];

  // System status derived from a system data reading's status code (source : Statuses.xlsx, KB2/KB3 panel,
  // plus additional codes observed from other KB panel variants).
  static systemStatusOptions: Option[] = [
    {value: '0', viewValue: 'System Off'},
    {value: '1', viewValue: 'System On'},
    {value: '2', viewValue: 'System Off + Flow Ok'},
    {value: '3', viewValue: 'System On + Flow Ok'},
    {value: '4', viewValue: 'System Off + No Flow'},
    {value: '5', viewValue: 'System On + No Flow'},
    {value: '6', viewValue: 'System Off + Flow Ok'},
    {value: '7', viewValue: 'System On + Flow Ok'},
    {value: '8', viewValue: 'System Off + Copper Short'},
    {value: '9', viewValue: 'System On + Copper Short'},
    {value: '10', viewValue: 'System Off + Copper Short + Flow Ok'},
    {value: '11', viewValue: 'System On + Copper Short + Flow Ok'},
    {value: '12', viewValue: 'System Off + Copper Short + No Flow'},
    {value: '13', viewValue: 'System On + Copper Short + No Flow'},
    {value: '15', viewValue: 'System On + Copper Short + Flow Ok'},
    {value: '16', viewValue: 'System Off + Silver Short'},
    {value: '17', viewValue: 'System On + Silver Short'},
    {value: '18', viewValue: 'System Off + Silver Short + Flow Ok'},
    {value: '19', viewValue: 'System On + Silver Short + Flow Ok'},
    {value: '20', viewValue: 'System Off + Silver Short + No Flow'},
    {value: '21', viewValue: 'System On + Silver Short + No Flow'},
    {value: '23', viewValue: 'System On + Silver Short + Flow Ok'},
    {value: '24', viewValue: 'System Off + Copper Short + Silver Short'},
    {value: '25', viewValue: 'System On + Copper Short + Silver Short'},
    {value: '26', viewValue: 'System Off + Copper Short + Silver Short + Flow Ok'},
    {value: '27', viewValue: 'System On + Copper Short + Silver Short + Flow Ok'},
    {value: '28', viewValue: 'System Off + Copper Short + Silver Short + No Flow'},
    {value: '29', viewValue: 'System On + Copper Short + Silver Short + No Flow'},
    {value: '32', viewValue: 'System Off + System Fault'},
    {value: '33', viewValue: 'System On + System Fault'},
    {value: '34', viewValue: 'System Off + Flow Ok + Hardware Fail'},
    {value: '36', viewValue: 'System Off + System Fault + No Flow'},
    {value: '37', viewValue: 'System On + System Fault + No Flow'},
    {value: '40', viewValue: 'System Off + Copper Short + System Fault'},
    {value: '41', viewValue: 'System On + Copper Short + System Fault'},
    {value: '44', viewValue: 'System Off + Copper Short + No Flow + System Fault'},
    {value: '45', viewValue: 'System On + Copper Short + No Flow + System Fault'},
    {value: '48', viewValue: 'System Off + Silver Short + System Fault'},
    {value: '49', viewValue: 'System On + Silver Short + System Fault'},
    {value: '52', viewValue: 'System Off + Silver Short + No Flow + System Fault'},
    {value: '53', viewValue: 'System On + Silver Short + No Flow + System Fault'},
    {value: '56', viewValue: 'System Off + Copper Short + Silver Short + System Fault'},
    {value: '57', viewValue: 'System On + Copper Short + Silver Short + System Fault'},
    {value: '58', viewValue: 'System Off + Copper Short + Silver Short + Flow Ok + Hardware Fail'},
    {value: '60', viewValue: 'System Off + Silver Short + Copper Short + No Flow + System Fault'},
    {value: '61', viewValue: 'System On + Silver Short + Copper Short + No Flow + System Fault'}
  ];

  static jobVisitTypesOptions: Option[] = [
    {value: VisitType.Sampling, viewValue: 'Sampling'},
    {value: VisitType.Maintenance, viewValue: 'Maintenance'},
    {value: VisitType.MaintenanceAndSampling, viewValue: 'Maintenance/Sampling'},
    {value: VisitType.RayMaintenance, viewValue: 'Ray Maintenance'},
    {value: VisitType.SamplingAndReef, viewValue: 'Sampling/Reef'},
    {value: VisitType.Reef, viewValue: 'Reef'},
    {value: VisitType.Other, viewValue: 'Other'}
  ];

  static jobStatuses: Option[] = [
    {value: JobStatus.ProvisionallyScheduled, viewValue: 'Provisionally Scheduled'},
    {value: JobStatus.Scheduled, viewValue: 'Scheduled'},
    {value: JobStatus.Notified, viewValue: 'Notified'},
    {value: JobStatus.SheetsCreated, viewValue: 'Sheets and KPI Created'},
    {value: JobStatus.InProgress, viewValue: 'In Progress'},
    {value: JobStatus.Completed, viewValue: 'Completed'},
    {value: JobStatus.ReportSent, viewValue: 'Report Sent'},
    {value: JobStatus.Invoiced, viewValue: 'Invoiced'},
    {value: JobStatus.Cancelled, viewValue: 'Cancelled'},
    {value: JobStatus.Off, viewValue: 'Off'},
    {value: JobStatus.OffNotPayable, viewValue: 'Off - Not Payable'}
  ];

  static contractLineStatuses: Option[] = [
    {value: ContractLineStatus.POReceived, viewValue: 'PO Received'},
    {value: ContractLineStatus.Scheduled, viewValue: 'Scheduled'},
    {value: ContractLineStatus.WorkComplete, viewValue: 'Work Complete'},
    {value: ContractLineStatus.ReportSent, viewValue: 'Report Sent'},
    {value: ContractLineStatus.InvoiceSent, viewValue: 'Invoice Sent'}
  ];

  static eventLogSourceOptions: Option[] = [
    {value: EventLogSource.Building, viewValue: 'Building'},
    {value: EventLogSource.BuildingSystem, viewValue: 'BuildingSystem'},
    {value: EventLogSource.Company, viewValue: 'Company'},
    {value: EventLogSource.Contract, viewValue: 'Contract'},
    {value: EventLogSource.Event, viewValue: 'Event'},
    {value: EventLogSource.FileDirectory, viewValue: 'FileDirectory'},
    {value: EventLogSource.Fleet, viewValue: 'Fleet'},
    {value: EventLogSource.InterimLink, viewValue: 'InterimLink'},
    {value: EventLogSource.Job, viewValue: 'Job'},
    {value: EventLogSource.JobRule, viewValue: 'JobRule'},
    {value: EventLogSource.Lab, viewValue: 'Lab'},
    {value: EventLogSource.Mail, viewValue: 'Mail'},
    {value: EventLogSource.NonConformity, viewValue: 'NonConformity'},
    {value: EventLogSource.Outlet, viewValue: 'Outlet'},
    {value: EventLogSource.ParentsAccount, viewValue: 'ParentsAccount'},
    {value: EventLogSource.PriceList, viewValue: 'PriceList'},
    {value: EventLogSource.Sample, viewValue: 'Sample'},
    {value: EventLogSource.SampleSet, viewValue: 'SampleSet'},
    {value: EventLogSource.SchedulerJobReports, viewValue: 'SchedulerJobReports'},
    {value: EventLogSource.ServiceReport, viewValue: 'ServiceReport'},    
    {value: EventLogSource.Site, viewValue: 'Site'},
    {value: EventLogSource.SiteBuilding, viewValue: 'SiteBuilding'},
    {value: EventLogSource.SiteContact, viewValue: 'SiteContact'},    
    {value: EventLogSource.SiteNote, viewValue: 'SiteNote'},  
    {value: EventLogSource.SiteRules, viewValue: 'SiteRules'},
    {value: EventLogSource.Stock, viewValue: 'Stock'},  
    {value: EventLogSource.StockAsset, viewValue: 'StockAsset'},
    {value: EventLogSource.StockRequest, viewValue: 'StockRequest'},  
    {value: EventLogSource.StockTransaction, viewValue: 'StockTransaction'},  
    {value: EventLogSource.Template, viewValue: 'Template'},
    {value: EventLogSource.TemplateOutlet, viewValue: 'TemplateOutlet'},
    {value: EventLogSource.User, viewValue: 'User'},
    {value: EventLogSource.VehicleCheck, viewValue: 'VehicleCheck'},
    {value: EventLogSource.VehicleRepair, viewValue: 'VehicleRepair'},
    {value: EventLogSource.WorkListItem, viewValue: 'WorkListItem'},
    {value: EventLogSource.WorkListSet, viewValue: 'WorkListSet'}
  ];

  static eventLogActionTypesOptions: Option[] = [
    {value: EventLogActionType.Create, viewValue: 'Create'},
    {value: EventLogActionType.Update, viewValue: 'Update'},
    {value: EventLogActionType.Delete, viewValue: 'Delete'}
  ];

  static workListTypesOptions: Option[] = [
    {value: WorkListType.Sampling, viewValue: 'Analytical Sampling'},
    {value: WorkListType.OrcaInstallation, viewValue: 'Orca Installation'},
    {value: WorkListType.OrcaCommissioning, viewValue: 'Orca Commissioning'},
    {value: WorkListType.ServiceReport, viewValue: 'Orca Maintenance'},
    {value: WorkListType.OrcaRepair, viewValue: 'Orca Repair'},
    {value: WorkListType.RayInstallation, viewValue: 'Ray Installation'},
    {value: WorkListType.RayCommissioning, viewValue: 'Ray Commissioning'},
    {value: WorkListType.RayMaintenance, viewValue: 'Ray Maintenance'},
    {value: WorkListType.RayRepair, viewValue: 'Ray Repair'},
    {value: WorkListType.Flushing, viewValue: 'Outlet Flushing'},
    {value: WorkListType.Temperature, viewValue: 'Temperature Monitoring'},
    {value: WorkListType.Failsafe, viewValue: 'TMV Service - Type 1 - Failsafe'},
    {value: WorkListType.ShowerClean, viewValue: 'Shower Head and Hose Descale and Disinfection'},
    {value: WorkListType.Maintenance, viewValue: 'TMV Service - Type 3 - Full Service'},
    {value: WorkListType.LegionellaRiskAssessment, viewValue: 'Legionella Risk Assessment'},
    {value: WorkListType.TmvServiceType2, viewValue: 'TMV Service - Type 2 - Service'},
    {value: WorkListType.IndependentTechnicalReport, viewValue: 'Independent Technical Report'},
    {value: WorkListType.TankInspection, viewValue: 'Cold Water Storage Tank Inspection'},
    {value: WorkListType.TankClean, viewValue: 'Cold Water Storage Tank Clean and Disinfection - Hard Surface Spray'},
    {value: WorkListType.TankCleanFillAndSoak, viewValue: 'Cold Water Storage Tank Clean and Disinfection - Fill and Soak'},
    {value: WorkListType.CalorifierInspection, viewValue: 'Hot Water Generator Inspection'},
    {value: WorkListType.CalorifierClean, viewValue: 'Hot Water Generator Clean and Disinfection'},
    {value: WorkListType.Blowdown, viewValue: 'Hot Water Generator Purge'},
    {value: WorkListType.ExpansionVesselFlushing, viewValue: 'Expansion Vessel Flushing'},
    {value: WorkListType.Disinfection, viewValue: 'Pipework Disinfection'},
    {value: WorkListType.LocalPipeworkDisinfection, viewValue: 'Local Pipework Disinfection'},
    {value: WorkListType.OutletDisinfection, viewValue: 'Outlet Disinfection'},
    {value: WorkListType.ChlorineDioxideInstallation, viewValue: 'Chlorine Dioxide (ClO²) Installation'},
    {value: WorkListType.ChlorineDioxideServicing, viewValue: 'Chlorine Dioxide (ClO²) Servicing'},
    {value: WorkListType.ChlorineDioxideInspection, viewValue: 'Chlorine Dioxide (ClO²) Inspection'},
    {value: WorkListType.TemperatureAndChlorineDioxide, viewValue: 'Chlorine Dioxide (ClO²) Monitoring'},
    {value: WorkListType.SodiumHypochloriteInstallation, viewValue: 'Sodium Hypochlorite Installation'},
    {value: WorkListType.SodiumHypochloriteCommissioning, viewValue: 'Sodium Hypochlorite Commissioning'},
    {value: WorkListType.SodiumHypochloriteServicing, viewValue: 'Sodium Hypochlorite Servicing'},
    {value: WorkListType.SodiumHypochloriteInspection, viewValue: 'Sodium Hypochlorite Inspection'},
    {value: WorkListType.SodiumHypochloriteMonitoring, viewValue: 'Sodium Hypochlorite Monitoring'},
    {value: WorkListType.SilverStabilisedHydrogenPeroxideInstallation, viewValue: 'Silver Stabilised Hydrogen Peroxide Installation'},
    {value: WorkListType.SilverStabilisedHydrogenPeroxideCommissioning, viewValue: 'Silver Stabilised Hydrogen Peroxide Commissioning'},
    {value: WorkListType.SilverStabilisedHydrogenPeroxideServicing, viewValue: 'Silver Stabilised Hydrogen Peroxide Servicing'},
    {value: WorkListType.SilverStabilisedHydrogenPeroxideInspection, viewValue: 'Silver Stabilised Hydrogen Peroxide Inspection'},
    {value: WorkListType.SilverStabilisedHydrogenPeroxideMonitoring, viewValue: 'Silver Stabilised Hydrogen Peroxide Monitoring'},
    {value: WorkListType.GenoxInstallation, viewValue: 'Genox Installation'},
    {value: WorkListType.GenoxCommissioning, viewValue: 'Genox Commissioning'},
    {value: WorkListType.GenoxServicing, viewValue: 'Genox Servicing'},
    {value: WorkListType.GenoxInspection, viewValue: 'Genox Inspection'},
    {value: WorkListType.GenoxMonitoring, viewValue: 'Genox Monitoring'},
    {value: WorkListType.UvSystemInstallation, viewValue: 'UV System Installation'},
    {value: WorkListType.UvSystemCommissioning, viewValue: 'UV System Commissioning'},
    {value: WorkListType.UvSystemServicing, viewValue: 'UV System Servicing'},
    {value: WorkListType.UvSystemInspection, viewValue: 'UV System Inspection'},
    {value: WorkListType.UvSystemMonitoring, viewValue: 'UV System Monitoring'},
    {value: WorkListType.WaterSoftenerInstallation, viewValue: 'Water Softener Installation'},
    {value: WorkListType.SoftenerService, viewValue: 'Water Softener Service'},
    {value: WorkListType.WaterSoftenerMonitoring, viewValue: 'Water Softener Monitoring'},
    {value: WorkListType.ClosedSystemSampling, viewValue: 'Closed System Sampling'},
    {value: WorkListType.ClosedSystemDosing, viewValue: 'Closed System Dosing'},
    {value: WorkListType.ClosedSystemFlushing, viewValue: 'Closed System Flushing'},
    {value: WorkListType.EvaporativeCoolingSystemsInspection, viewValue: 'Evaporative Cooling Systems Inspection'},
    {value: WorkListType.EvaporativeCoolingSystemsCleaningAndDisinfection, viewValue: 'Evaporative Cooling Systems Cleaning and Disinfection'},
    {value: WorkListType.RemedialWorks, viewValue: 'Remedial Works'},
    {value: WorkListType.RemoteMonitoringInstallation, viewValue: 'Remote Monitoring Installation'},
    {value: WorkListType.AddPouFilter, viewValue: 'Point of Use Filter Installation'},
    {value: WorkListType.ReplacePouFilter, viewValue: 'Point of Use Filter Replacement'},
    {value: WorkListType.RemovePouFilter, viewValue: 'Point of Use Filter Removal'},
    {value: WorkListType.Other, viewValue: 'Other'}
  ];

  static samplingFlexibilities: Option[] = [
    {value: 'flexible', viewValue: 'Flexible'},
    {value: 'fixed', viewValue: 'Fixed'}
  ];

  static reportingMethods: Option[] = [
    {value: 'tetras', viewValue: 'Tetras'},
    {value: 'trueContext', viewValue: 'True Context'},
    {value: 'paperBased', viewValue: 'Paper Based'},
    {value: 'other', viewValue: 'Other'}
  ];

  static jobFrequencies: Option[] = [
    {value: JobFrequency.Weekly, viewValue: 'Weekly'},
    {value: JobFrequency.Biweekly, viewValue: 'Biweekly'},
    {value: JobFrequency.Monthly, viewValue: 'Monthly'},
    {value: JobFrequency.Bimonthly, viewValue: 'Bimonthly'},
    {value: JobFrequency.Quarterly, viewValue: 'Quarterly'},
    {value: JobFrequency.SixMonthly, viewValue: '6-Monthly'},
    {value: JobFrequency.Yearly, viewValue: 'Yearly'},
    {value: JobFrequency.OneOff, viewValue: 'One-Off'}
  ];
  
  static months = [
    {value: 1, viewValue: 'January'},
    {value: 2, viewValue: 'February'},
    {value: 3, viewValue: 'March'},
    {value: 4, viewValue: 'April'},
    {value: 5, viewValue: 'May'},
    {value: 6, viewValue: 'June'},
    {value: 7, viewValue: 'July'},
    {value: 8, viewValue: 'August'},
    {value: 9, viewValue: 'September'},
    {value: 10, viewValue: 'October'},
    {value: 11, viewValue: 'November'},
    {value: 12, viewValue: 'December'}
  ];

  static kpiIssueReasons: Option[] = [
    {value: 'NoAccessFixedSampling', viewValue: "No access - fixed sampling"},
    {value: 'NoAccessSiteRefusedAccess', viewValue: "No access - site refused access"},
    {value: 'PartialAccessRoomsInUse', viewValue: "Partial access - rooms in use"},
    {value: 'KpiMismatch', viewValue: "KPI doesn't match the job sheet"},
    {value: 'JobDiffers', viewValue: "Job differs to what is scheduled"},
    {value: 'AccessRestrictions', viewValue: "Access restrictions"},
    {value: 'DuplicatedLocations', viewValue: "Duplicated locations"},
    {value: 'TechnicalIssue', viewValue: "Technical issue"}
  ];
}
